'use client';

import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Search, Filter, SortAsc, SortDesc, ChevronLeft, ChevronRight,
  RefreshCw, Download, Trash2, CheckSquare, ExternalLink, Eye,
  AlertTriangle, X, ChevronDown, Sparkles,
} from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import IncidentDetailPanel from './IncidentDetailPanel';

type Severity = 'p1' | 'p2' | 'p3' | 'p4';
type IncidentStatus = 'detected' | 'classified' | 'remediating' | 'verifying' | 'resolved' | 'closed';

interface Incident {
  id: string;
  title: string;
  severity: Severity;
  type: string;
  node: string;
  region: string;
  aiClassification: string;
  aiConfidence: number;
  status: IncidentStatus;
  slaDeadline: string;
  slaBreaching: boolean;
  snoTicket: string | null;
  remediationStatus: 'none' | 'running' | 'success' | 'failed';
  assignee: string;
  detectedAt: string;
  customer: string;
}

const incidents: Incident[] = [
  { id: 'INC-4821', title: 'Remote node receiver offline — 18 residential units affected', severity: 'p1', type: 'Remote Node Down', node: 'RN-BOLE-APT-07', region: 'Bole', aiClassification: 'REMOTE_NODE_UNREACHABLE', aiConfidence: 97, status: 'remediating', slaDeadline: '09:44:00', slaBreaching: true, snoTicket: 'INC0047821', remediationStatus: 'running', assignee: 'Dawit Taddese', detectedAt: '09:14:22', customer: 'Maple Court Apartments (MDU — 18 residents)' },
  { id: 'INC-4820', title: 'Base node RF link degraded — SNR below threshold', severity: 'p1', type: 'RF Link Degradation', node: 'BN-KIRKOS-WEST-04', region: 'Kirkos', aiClassification: 'RF_LINK_DEGRADED', aiConfidence: 99, status: 'detected', slaDeadline: '09:37:00', slaBreaching: true, snoTicket: null, remediationStatus: 'none', assignee: 'Selam Bekele', detectedAt: '09:22:44', customer: 'EuroTelco GmbH (Business Complex — 14 tenants)' },
  { id: 'INC-4819', title: 'Remote node receiver unreachable — ICMP timeout all probes', severity: 'p1', type: 'Remote Node Down', node: 'RN-YEKA-BIZ-02', region: 'Yeka', aiClassification: 'NODE_UNREACHABLE', aiConfidence: 99, status: 'classified', slaDeadline: '09:29:00', slaBreaching: false, snoTicket: 'INC0047819', remediationStatus: 'none', assignee: 'Yonas Girma', detectedAt: '09:14:31', customer: 'Bayview Business Park (Business Complex — 9 tenants)' },
  { id: 'INC-4818', title: 'Tarana base node beam-forming instability — sector oscillation', severity: 'p2', type: 'Beam-Forming Instability', node: 'BN-ARADA-NORTH-01', region: 'Arada', aiClassification: 'BEAMFORMING_INSTABILITY', aiConfidence: 84, status: 'remediating', slaDeadline: '10:21:00', slaBreaching: false, snoTicket: 'INC0047818', remediationStatus: 'running', assignee: 'Hiwot Alemu', detectedAt: '09:21:03', customer: 'PacificNet Ltd. (Business Complex — 12 tenants)' },
  { id: 'INC-4817', title: 'Upstream packet loss exceeds 3% — FWA backhaul link', severity: 'p2', type: 'Backhaul Packet Loss', node: 'BN-LIDETA-SOUTH-03', region: 'Lideta', aiClassification: 'UPSTREAM_PACKET_LOSS', aiConfidence: 91, status: 'verifying', slaDeadline: '10:19:00', slaBreaching: false, snoTicket: 'INC0047817', remediationStatus: 'success', assignee: 'Dawit Taddese', detectedAt: '09:19:57', customer: 'Axiom Networks (Single Dwelling Business)' },
  { id: 'INC-4816', title: 'Remote node Rx power below minimum — signal path obstruction', severity: 'p2', type: 'Signal Degradation', node: 'RN-KOLFE-APT-05', region: 'Kolfe Keranio', aiClassification: 'RX_POWER_LOW', aiConfidence: 88, status: 'classified', slaDeadline: '10:16:00', slaBreaching: false, snoTicket: 'INC0047816', remediationStatus: 'none', assignee: 'Bereket Haile', detectedAt: '09:16:44', customer: 'Orchid Gardens Residences (MDU — 20 residents)' },
  { id: 'INC-4815', title: 'Base node CPU spike — scheduling overload on sector processor', severity: 'p2', type: 'CPU Overload', node: 'BN-NIFAS-EAST-01', region: 'Nifas Silk-Lafto', aiClassification: 'CPU_OVERLOAD', aiConfidence: 78, status: 'remediating', slaDeadline: '10:18:00', slaBreaching: false, snoTicket: 'INC0047815', remediationStatus: 'running', assignee: 'Selam Bekele', detectedAt: '09:18:22', customer: 'Axiom Networks (Single Dwelling Business)' },
  { id: 'INC-4814', title: 'Remote node hardware warning — thermal sensor above threshold', severity: 'p3', type: 'Hardware Warning', node: 'RN-GULLELE-APT-12', region: 'Gullele', aiClassification: 'HARDWARE_WARNING', aiConfidence: 65, status: 'classified', slaDeadline: '12:12:00', slaBreaching: false, snoTicket: null, remediationStatus: 'none', assignee: 'Yonas Girma', detectedAt: '09:12:08', customer: 'Lakeside Apartments (MDU — 7 residents)' },
  { id: 'INC-4813', title: 'Base node memory pressure — FWA session table near capacity', severity: 'p3', type: 'Memory Pressure', node: 'BN-ADDISKETEMA-WEST-01', region: 'Addis Ketema', aiClassification: 'MEMORY_PRESSURE', aiConfidence: 72, status: 'resolved', slaDeadline: '12:08:00', slaBreaching: false, snoTicket: 'INC0047813', remediationStatus: 'success', assignee: 'Tigist Worku', detectedAt: '09:08:51', customer: 'BritNet Wholesale (Business Complex — 11 tenants)' },
  { id: 'INC-4812', title: 'QoS policy violation — residential vs business traffic remarking', severity: 'p3', type: 'QoS Violation', node: 'BN-AKAKI-CENTRAL-02', region: 'Akaki Kality', aiClassification: 'QOS_VIOLATION', aiConfidence: 81, status: 'resolved', slaDeadline: '11:55:00', slaBreaching: false, snoTicket: 'INC0047812', remediationStatus: 'success', assignee: 'Abebe Mekonen', detectedAt: '08:55:14', customer: 'RhineNet GmbH (Business Complex — 6 tenants)' },
  { id: 'INC-4811', title: 'Remote node NTP sync lost — FWA timing reference drift >500ms', severity: 'p4', type: 'NTP Drift', node: 'RN-LEMIKURA-BIZ-01', region: 'Lemi Kura', aiClassification: 'NTP_DRIFT', aiConfidence: 93, status: 'closed', slaDeadline: '10:30:00', slaBreaching: false, snoTicket: 'INC0047811', remediationStatus: 'success', assignee: 'Dawit Taddese', detectedAt: '08:30:22', customer: 'SunCoast ISP (Single Dwelling Business)' },
  { id: 'INC-4810', title: 'Base node syslog flooding — 12k events/min from FWA scheduler', severity: 'p4', type: 'Log Flood', node: 'BN-BOLE-NORTH-01', region: 'Bole', aiClassification: 'SYSLOG_FLOOD', aiConfidence: 87, status: 'closed', slaDeadline: '10:15:00', slaBreaching: false, snoTicket: 'INC0047810', remediationStatus: 'success', assignee: 'Hiwot Alemu', detectedAt: '08:15:09', customer: 'AustraliaPTY (Single Dwelling Residential)' },
];

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50];

type SortKey = keyof Incident;

export default function IncidentTableSection() {
  const [search, setSearch] = useState('');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [regionFilter, setRegionFilter] = useState<string>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sortKey, setSortKey] = useState<SortKey>('id');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [detailId, setDetailId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    return incidents
      .filter((i) => {
        const q = search.toLowerCase();
        const matchSearch =
          !q ||
          i.id.toLowerCase().includes(q) ||
          i.title.toLowerCase().includes(q) ||
          i.node.toLowerCase().includes(q) ||
          i.aiClassification.toLowerCase().includes(q);
        const matchSev = severityFilter === 'all' || i.severity === severityFilter;
        const matchStatus = statusFilter === 'all' || i.status === statusFilter;
        const matchRegion = regionFilter === 'all' || i.region === regionFilter;
        return matchSearch && matchSev && matchStatus && matchRegion;
      })
      .sort((a, b) => {
        const av = String(a[sortKey]);
        const bv = String(b[sortKey]);
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      });
  }, [search, severityFilter, statusFilter, regionFilter, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  const toggleRow = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === paginated.length) setSelected(new Set());
    else setSelected(new Set(paginated.map((i) => i.id)));
  };

  const handleBulkClose = () => {
    toast.success(`${selected.size} incidents marked as closed`);
    setSelected(new Set());
  };

  const handleBulkAssign = () => {
    toast.info(`Assigning ${selected.size} incidents…`);
  };

  const detailIncident = incidents.find((i) => i.id === detailId) ?? null;

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <SortAsc size={11} className="text-muted opacity-40" />;
    return sortDir === 'asc' ? <SortAsc size={11} className="text-primary" /> : <SortDesc size={11} className="text-primary" />;
  };

  const regions = Array.from(new Set(incidents.map((i) => i.region)));

  return (
    <div className="flex gap-5 relative">
      <div className={`flex-1 min-w-0 transition-all duration-300 ${detailId ? 'mr-0' : ''}`}>
        {/* Filters */}
        <div className="bg-surface border border-border rounded-xl p-4 mb-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                type="text"
                placeholder="Search incidents, nodes, classifications…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="w-full pl-8 pr-3 py-2 text-sm bg-surface-raised border border-border rounded-lg text-foreground placeholder:text-muted focus:outline-none focus:border-primary/50 transition-colors"
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Filter size={13} className="text-muted" />
              {/* Severity */}
              <div className="relative">
                <select
                  value={severityFilter}
                  onChange={(e) => { setSeverityFilter(e.target.value); setPage(1); }}
                  className="text-xs bg-surface-raised border border-border rounded-lg pl-3 pr-7 py-2 text-foreground-muted focus:outline-none focus:border-primary/50 appearance-none cursor-pointer"
                >
                  <option value="all">All Severities</option>
                  <option value="p1">P1 — Critical</option>
                  <option value="p2">P2 — High</option>
                  <option value="p3">P3 — Medium</option>
                  <option value="p4">P4 — Low</option>
                </select>
                <ChevronDown size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
              </div>

              {/* Status */}
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                  className="text-xs bg-surface-raised border border-border rounded-lg pl-3 pr-7 py-2 text-foreground-muted focus:outline-none focus:border-primary/50 appearance-none cursor-pointer"
                >
                  <option value="all">All Statuses</option>
                  <option value="detected">Detected</option>
                  <option value="classified">Classified</option>
                  <option value="remediating">Remediating</option>
                  <option value="verifying">Verifying</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
                <ChevronDown size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
              </div>

              {/* Region */}
              <div className="relative">
                <select
                  value={regionFilter}
                  onChange={(e) => { setRegionFilter(e.target.value); setPage(1); }}
                  className="text-xs bg-surface-raised border border-border rounded-lg pl-3 pr-7 py-2 text-foreground-muted focus:outline-none focus:border-primary/50 appearance-none cursor-pointer"
                >
                  <option value="all">All Regions</option>
                  {regions.map((r) => (
                    <option key={`region-opt-${r}`} value={r}>{r}</option>
                  ))}
                </select>
                <ChevronDown size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
              </div>
            </div>

            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => toast.info('Refreshing incidents…')}
                className="p-2 rounded-lg hover:bg-surface-raised text-muted hover:text-foreground transition-all active:scale-95"
                title="Refresh"
              >
                <RefreshCw size={14} />
              </button>
              <button
                onClick={() => toast.info('Exporting incidents CSV…')}
                className="p-2 rounded-lg hover:bg-surface-raised text-muted hover:text-foreground transition-all active:scale-95"
                title="Export CSV"
              >
                <Download size={14} />
              </button>
            </div>
          </div>

          {/* Stats row */}
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border">
            {[
              { label: 'P1 Active', val: incidents.filter(i => i.severity === 'p1' && i.status !== 'resolved' && i.status !== 'closed').length, color: 'text-danger' },
              { label: 'P2 Active', val: incidents.filter(i => i.severity === 'p2' && i.status !== 'resolved' && i.status !== 'closed').length, color: 'text-warning' },
              { label: 'Remediating', val: incidents.filter(i => i.status === 'remediating').length, color: 'text-primary' },
              { label: 'SLA at Risk', val: incidents.filter(i => i.slaBreaching).length, color: 'text-danger' },
              { label: 'Total', val: incidents.length, color: 'text-foreground' },
            ].map((s) => (
              <div key={`stat-${s.label}`} className="flex items-center gap-1.5">
                <span className={`font-mono font-bold text-sm ${s.color}`}>{s.val}</span>
                <span className="text-[11px] text-muted">{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div className="bg-primary/10 border border-primary/30 rounded-xl px-4 py-2.5 mb-3 flex items-center gap-3 animate-slide-up">
            <CheckSquare size={14} className="text-primary" />
            <span className="text-sm font-medium text-primary">{selected.size} selected</span>
            <div className="flex items-center gap-2 ml-2">
              <button
                onClick={handleBulkClose}
                className="text-xs bg-surface-raised border border-border px-3 py-1.5 rounded-lg text-foreground hover:border-primary/40 transition-colors"
              >
                Close All
              </button>
              <button
                onClick={handleBulkAssign}
                className="text-xs bg-surface-raised border border-border px-3 py-1.5 rounded-lg text-foreground hover:border-primary/40 transition-colors"
              >
                Assign
              </button>
              <button
                onClick={() => toast.error(`${selected.size} incidents escalated to P1`)}
                className="text-xs bg-danger/15 border border-danger/30 px-3 py-1.5 rounded-lg text-danger hover:bg-danger/25 transition-colors"
              >
                Escalate
              </button>
            </div>
            <button onClick={() => setSelected(new Set())} className="ml-auto text-muted hover:text-foreground">
              <X size={14} />
            </button>
          </div>
        )}

        {/* Table */}
        <div className="bg-surface border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-surface-raised/50">
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={selected.size === paginated.length && paginated.length > 0}
                      onChange={toggleAll}
                      className="w-3.5 h-3.5 rounded accent-primary cursor-pointer"
                    />
                  </th>
                  {[
                    { key: 'id' as SortKey, label: 'ID' },
                    { key: 'severity' as SortKey, label: 'SEV' },
                    { key: 'title' as SortKey, label: 'Title' },
                    { key: 'type' as SortKey, label: 'Type' },
                    { key: 'node' as SortKey, label: 'Node' },
                    { key: 'region' as SortKey, label: 'Region' },
                    { key: 'aiClassification' as SortKey, label: 'AI Class' },
                    { key: 'status' as SortKey, label: 'Status' },
                    { key: 'slaDeadline' as SortKey, label: 'SLA' },
                    { key: 'assignee' as SortKey, label: 'Assignee' },
                  ].map(({ key, label }) => (
                    <th
                      key={`th-inc-${key}`}
                      onClick={() => toggleSort(key)}
                      className="px-3 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-muted whitespace-nowrap cursor-pointer hover:text-foreground transition-colors select-none"
                    >
                      <div className="flex items-center gap-1">
                        {label}
                        <SortIcon col={key} />
                      </div>
                    </th>
                  ))}
                  <th className="px-3 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-muted">SNow</th>
                  <th className="px-3 py-3 w-16" />
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={13} className="px-4 py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <AlertTriangle size={32} className="text-muted" />
                        <p className="text-sm font-medium text-foreground-muted">No incidents match your filters</p>
                        <p className="text-xs text-muted">Adjust the severity, status, or region filters above</p>
                        <button
                          onClick={() => { setSearch(''); setSeverityFilter('all'); setStatusFilter('all'); setRegionFilter('all'); }}
                          className="text-xs text-primary hover:underline"
                        >
                          Clear all filters
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginated.map((inc, idx) => (
                    <tr
                      key={inc.id}
                      className={`border-b border-border/40 transition-colors group
                        ${idx % 2 === 1 ? 'bg-surface-raised/20' : ''}
                        ${selected.has(inc.id) ? 'bg-primary/5' : 'hover:bg-surface-raised/50'}
                        ${inc.slaBreaching ? 'border-l-2 border-l-danger' : ''}
                      `}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selected.has(inc.id)}
                          onChange={() => toggleRow(inc.id)}
                          className="w-3.5 h-3.5 rounded accent-primary cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-3 font-mono font-semibold text-primary whitespace-nowrap">{inc.id}</td>
                      <td className="px-3 py-3">
                        <StatusBadge status={inc.severity} />
                      </td>
                      <td className="px-3 py-3 max-w-[220px]">
                        <p className="text-foreground font-medium truncate" title={inc.title}>{inc.title}</p>
                        {inc.slaBreaching && (
                          <span className="text-[9px] text-danger font-bold uppercase tracking-wide">
                            ⚠ SLA BREACH IMMINENT
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-foreground-muted whitespace-nowrap">{inc.type}</td>
                      <td className="px-3 py-3 font-mono text-[11px] text-foreground whitespace-nowrap">{inc.node}</td>
                      <td className="px-3 py-3 text-foreground-muted whitespace-nowrap">{inc.region}</td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono text-[10px] text-foreground-muted whitespace-nowrap">{inc.aiClassification}</span>
                          <span className={`text-[10px] font-bold ${
                            inc.aiConfidence >= 85 ? 'text-success' :
                            inc.aiConfidence >= 65 ? 'text-primary' : 'text-warning'
                          }`}>{inc.aiConfidence}%</span>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <StatusBadge status={inc.status} />
                      </td>
                      <td className="px-3 py-3 font-mono text-[11px] whitespace-nowrap">
                        <span className={inc.slaBreaching ? 'text-danger font-bold' : 'text-foreground-muted'}>
                          {inc.slaDeadline}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-foreground-muted whitespace-nowrap">{inc.assignee}</td>
                      <td className="px-3 py-3">
                        {inc.snoTicket ? (
                          <a
                            href="#"
                            onClick={(e) => e.preventDefault()}
                            className="flex items-center gap-1 text-[10px] text-primary hover:underline font-mono whitespace-nowrap"
                          >
                            {inc.snoTicket}
                            <ExternalLink size={9} />
                          </a>
                        ) : (
                          <button
                            onClick={() => toast.info(`Creating ServiceNow ticket for ${inc.id}…`)}
                            className="text-[10px] text-muted hover:text-primary transition-colors whitespace-nowrap"
                          >
                            + Create
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => setDetailId(inc.id === detailId ? null : inc.id)}
                            className="p-1 rounded hover:bg-surface-overlay text-muted hover:text-primary transition-colors"
                            title="View details"
                          >
                            <Eye size={13} />
                          </button>
                          <button
                            onClick={() => { setDetailId(inc.id); }}
                            className="p-1 rounded hover:bg-surface-overlay text-muted hover:text-primary transition-colors"
                            title="AI Analysis"
                          >
                            <Sparkles size={13} />
                          </button>
                          <button
                            onClick={() => toast.error(`Deleting ${inc.id}…`)}
                            className="p-1 rounded hover:bg-surface-overlay text-muted hover:text-danger transition-colors"
                            title="Delete incident"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-surface-raised/30">
            <div className="flex items-center gap-3 text-xs text-muted">
              <span>
                Showing {((page - 1) * perPage) + 1}–{Math.min(page * perPage, filtered.length)} of {filtered.length}
              </span>
              <div className="flex items-center gap-1.5">
                <span>Per page:</span>
                <div className="relative">
                  <select
                    value={perPage}
                    onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                    className="text-xs bg-surface-raised border border-border rounded px-2 py-1 text-foreground focus:outline-none focus:border-primary/50 appearance-none pr-5 cursor-pointer"
                  >
                    {ITEMS_PER_PAGE_OPTIONS.map((n) => (
                      <option key={`pp-${n}`} value={n}>{n}</option>
                    ))}
                  </select>
                  <ChevronDown size={10} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                className="px-2 py-1 rounded text-[11px] text-muted hover:text-foreground hover:bg-surface-raised disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                «
              </button>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded text-muted hover:text-foreground hover:bg-surface-raised disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft size={13} />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pg = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                return (
                  <button
                    key={`page-${pg}`}
                    onClick={() => setPage(pg)}
                    className={`w-7 h-7 rounded text-[11px] font-medium transition-all
                      ${pg === page ? 'bg-primary/20 text-primary border border-primary/30' : 'text-muted hover:text-foreground hover:bg-surface-raised'}`}
                  >
                    {pg}
                  </button>
                );
              })}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded text-muted hover:text-foreground hover:bg-surface-raised disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <ChevronRight size={13} />
              </button>
              <button
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
                className="px-2 py-1 rounded text-[11px] text-muted hover:text-foreground hover:bg-surface-raised disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                »
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Detail panel */}
      {detailIncident && (
        <IncidentDetailPanel
          incident={detailIncident}
          onClose={() => setDetailId(null)}
        />
      )}
    </div>
  );
}